/* ===========================================================
   Rossi Product Page v2 — Upgrade Scripts
   Scope: runs only on body.template-v2
   =========================================================== */
(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function init() {
    if (!document.body || !document.body.classList.contains('template-v2')) return;
    bindQuickShop();
    bindMakeOfferModal();
    bindOfferFormSubmit();
    bindScrollReveals();
    bindStockCardAnimation();
    bindTabs();
    bindDescriptionClamp();
    bindShipPromise();
    bindStickyBar();
    /* bindRioGalleryGuard removed — capture-phase stopPropagation broke Rio swiper bubbling */
    bindWishlistButton();
  }

  /* ===== OFFER FORM SUBMIT — Hidden iframe target =====
     AJAX submit gets blocked by Shopify hCaptcha ("Missing CAPTCHA token").
     Shopify.captcha.protect() exists but only works reliably for native
     form submit (not programmatic), so AJAX still 400s.

     Solution: form has target="rossi-offer-target" pointing at hidden iframe.
     Native browser submit → Shopify hCaptcha auto-attaches → token injected
     into form → POST goes into iframe → page never navigates.

     We listen for iframe load event to detect completion. First load is
     initial blank doc (skip via flag). Second load = post-submit response,
     read iframe URL to determine success vs error. */
  function bindOfferFormSubmit() {
    var form = document.getElementById('rossi-offer-form');
    if (!form) return;
    var iframe = document.getElementById('rossi-offer-iframe');
    if (!iframe) return;

    // Belt-and-suspenders: ensure form targets the iframe even if Liquid
    // form tag stripped the `target` attribute. Native browser submit will
    // route response into iframe so page never reloads.
    form.setAttribute('target', 'rossi-offer-target');

    var modalEl = document.getElementById('rossi-make-offer-modal');
    var formWrap = modalEl && modalEl.querySelector('.rossi-modal__form');
    var successEl = modalEl && modalEl.querySelector('.rossi-modal__success');
    var errorEl = form.querySelector('[data-rossi-offer-error]');
    var submitBtn = form.querySelector('button[type="submit"]');
    var originalLabel = submitBtn ? submitBtn.textContent : 'Submit Offer';

    var pendingSubmit = false;

    function setBusy(busy) {
      if (!submitBtn) return;
      if (busy) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.textContent = 'Sending…';
      } else {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
        submitBtn.textContent = originalLabel;
      }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || 'Submission failed. Please try again, or email us directly.';
      errorEl.classList.add('is-visible');
    }

    function clearError() {
      if (!errorEl) return;
      errorEl.classList.remove('is-visible');
      errorEl.textContent = '';
    }

    function showSuccess() {
      if (formWrap) formWrap.classList.add('is-hidden');
      if (successEl) {
        successEl.classList.remove('is-visible');
        /* eslint-disable-next-line no-unused-expressions */
        successEl.offsetHeight;
        successEl.classList.add('is-visible');
      }
      form.reset();
    }

    form.addEventListener('submit', function () {
      // Allow native submit. target="rossi-offer-target" routes response
      // into hidden iframe — page stays put.
      clearError();
      setBusy(true);
      pendingSubmit = true;
    });

    iframe.addEventListener('load', function () {
      if (!pendingSubmit) return; // ignore initial blank document load
      pendingSubmit = false;

      var iframeUrl = '';
      var iframeBodyText = '';
      var iframeTitle = '';
      var crossOriginBlocked = false;
      try {
        iframeUrl = iframe.contentWindow.location.href || '';
        iframeBodyText = (iframe.contentDocument && iframe.contentDocument.body
                          ? iframe.contentDocument.body.textContent : '') || '';
        iframeTitle = (iframe.contentDocument && iframe.contentDocument.title) || '';
      } catch (err) {
        crossOriginBlocked = true;
      }

      setBusy(false);

      // Cross-origin block usually means Shopify redirected away — treat as success.
      if (crossOriginBlocked) {
        showSuccess();
        try { iframe.contentWindow.location.replace('about:blank'); } catch (e) {}
        return;
      }

      var lowerBody = iframeBodyText.toLowerCase();
      var lowerTitle = iframeTitle.toLowerCase();

      // STRONG error signals — only block success on explicit error indicators
      var captchaErr = lowerTitle.indexOf('captcha') !== -1 ||
                       lowerBody.indexOf('missing captcha') !== -1 ||
                       lowerBody.indexOf('captcha token') !== -1;

      var serverErr = lowerTitle.indexOf('400') !== -1 ||
                      lowerTitle.indexOf('500') !== -1 ||
                      lowerTitle.indexOf('502') !== -1 ||
                      lowerTitle.indexOf('503') !== -1;

      // Shopify validation errors render visible .errors block
      var validationErr = false;
      try {
        var errsEl = iframe.contentDocument &&
                     iframe.contentDocument.querySelector('.errors, .form__message--error, [data-form-errors]');
        if (errsEl && errsEl.offsetParent !== null) validationErr = true;
      } catch (e) {}

      if (captchaErr) {
        showError('Verification failed. Please refresh the page and try again.');
      } else if (validationErr) {
        showError('Please check all required fields and try again.');
      } else if (serverErr) {
        showError('Server error. Please try again or email us directly.');
      } else {
        // Permissive success — any non-error iframe load means Shopify accepted it.
        // Covers: 302 → product page, 200 product render, /contact-success, etc.
        showSuccess();
      }

      // Reset iframe so next submit fires a fresh load event
      try { iframe.contentWindow.location.replace('about:blank'); } catch (e) {}
    });
  }

  /* ===== WISHLIST BUTTON =====
     Visual heart toggle next to product title.
     Persists state in localStorage.
     Forwards to Swym SDK or Zooomy if loaded.
  */
  function bindWishlistButton() {
    var btn = document.querySelector('[data-rossi-wishlist]');
    if (!btn) return;
    var handle = btn.getAttribute('data-product-handle') || '';
    var pid = btn.getAttribute('data-product-id') || '';
    var vid = btn.getAttribute('data-variant-id') || '';

    function loadState() {
      try { return JSON.parse(localStorage.getItem('rossi_wishlist') || '{}'); }
      catch (e) { return {}; }
    }
    function saveState(s) {
      try { localStorage.setItem('rossi_wishlist', JSON.stringify(s)); } catch (e) {}
    }

    var state = loadState();
    if (state[handle]) btn.setAttribute('aria-pressed', 'true');

    btn.addEventListener('click', function () {
      var pressed = btn.getAttribute('aria-pressed') === 'true';
      var next = !pressed;
      btn.setAttribute('aria-pressed', next ? 'true' : 'false');
      var s = loadState();
      if (next) s[handle] = { pid: pid, vid: vid, ts: Date.now() };
      else delete s[handle];
      saveState(s);

      showRossiToast(next ? 'Saved to wishlist' : 'Removed from wishlist');

      if (window.SwymCallbacks && Array.isArray(window.SwymCallbacks)) {
        window.SwymCallbacks.push(function (swat) {
          if (!swat || !swat.addToWishList) return;
          var payload = { epi: parseInt(vid, 10), empi: parseInt(pid, 10), du: location.href };
          if (next) swat.addToWishList(payload, function () {}, function () {});
          else swat.removeFromWishList(payload, function () {}, function () {});
        });
      }
      if (window.ZooomyApi && typeof window.ZooomyApi.toggle === 'function') {
        try { window.ZooomyApi.toggle({ productId: pid, variantId: vid, on: next }); } catch (e) {}
      }
    });
  }

  /* ===== TOAST =====
     Lightweight bottom-center toast for wishlist + future actions.
  */
  var _rossiToastEl = null;
  var _rossiToastTimer = null;
  function showRossiToast(message) {
    if (!_rossiToastEl) {
      _rossiToastEl = document.createElement('div');
      _rossiToastEl.className = 'rossi-toast';
      _rossiToastEl.setAttribute('role', 'status');
      _rossiToastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(_rossiToastEl);
    }
    _rossiToastEl.textContent = message;
    _rossiToastEl.classList.add('is-visible');
    if (_rossiToastTimer) clearTimeout(_rossiToastTimer);
    _rossiToastTimer = setTimeout(function () {
      _rossiToastEl.classList.remove('is-visible');
    }, 2400);
  }

  /* ===== RIO GALLERY GUARD — REMOVED =====
     Previously used capture-phase e.stopPropagation() on .rio-media-gallery clicks.
     This killed click bubbling that Rio swiper relies on for nav arrow + thumbnail
     pagination. Resulted in broken slide navigation after theme code updates.
     Theme lightbox may now open on thumbnail click — acceptable trade-off.
     If lightbox-suppression is needed later, target the lightbox handler directly
     instead of stopping all propagation.
  */

  /* ===== DESCRIPTION CLAMP (Read More / Read Less + fade) =====
     Wraps long product descriptions in a fixed-height container with a
     bottom-fade gradient and a toggle. Only enables clamp when content
     actually overflows the collapsed height — short descriptions render
     as-is with no toggle. */
  function bindDescriptionClamp() {
    var containers = document.querySelectorAll('[data-rossi-clamp]');
    if (!containers.length) return;

    function measureAndApply(container) {
      var content = container.querySelector('[data-rossi-clamp-content]');
      var toggle = container.querySelector('[data-rossi-clamp-toggle]');
      if (!content || !toggle) return;

      // Reset to natural state for accurate measurement
      var wasClamped = container.classList.contains('is-clamped');
      var wasExpanded = container.classList.contains('is-expanded');
      container.classList.remove('is-clamped', 'is-expanded');

      // Match CSS: max-height: clamp(120px, 18vh, 180px)
      // Fluid scaling — no static breakpoint, works on any device size.
      var collapsedCap = Math.min(180, Math.max(120, Math.round(window.innerHeight * 0.18)));
      var fullHeight = content.scrollHeight;

      // Reveal toggle whenever content actually exceeds the cap (no buffer).
      // Even small overflows on desktop should show Read More so the layout
      // stays consistent across viewports.
      if (fullHeight > collapsedCap) {
        container.classList.add('is-clamped');
        toggle.hidden = false;
        if (wasExpanded) {
          container.classList.add('is-expanded');
        }
      } else {
        toggle.hidden = true;
      }
    }

    containers.forEach(function (container) {
      measureAndApply(container);

      var toggle = container.querySelector('[data-rossi-clamp-toggle]');
      var moreLabel = container.querySelector('[data-rossi-clamp-label-more]');
      var lessLabel = container.querySelector('[data-rossi-clamp-label-less]');
      if (!toggle) return;

      toggle.addEventListener('click', function () {
        var expanded = container.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (moreLabel) moreLabel.hidden = expanded;
        if (lessLabel) lessLabel.hidden = !expanded;
      });
    });

    // Re-measure on resize (debounced)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        containers.forEach(measureAndApply);
      }, 200);
    });
  }

  /* ===== TABS (Description / Shipping & Returns) ===== */
  function bindTabs() {
    var tabs = document.querySelectorAll('.rossi-tabs__tab');
    if (!tabs.length) return;
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var key = tab.getAttribute('data-rossi-tab');
        if (!key) return;
        var nav = tab.closest('.rossi-tabs__nav');
        var wrap = tab.closest('.rossi-below-fold__tabs');
        if (!wrap || !nav) return;
        nav.querySelectorAll('.rossi-tabs__tab').forEach(function (t) {
          var active = t === tab;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        wrap.querySelectorAll('.rossi-tabs__panel').forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-rossi-panel') === key);
        });
      });
    });
  }

  /* ===== SHIP PROMISE PILL + MODAL =====
     Variant-aware shipping pill. Renders "Free Shipping" if variant price
     below threshold; "Free White Glove Delivery Included" if at/above.
     Click pill → centered modal with policy text. Vanilla — no magnific.
     Modal element is created once on first open and reused across all pills.
     Built without innerHTML (createElement + textContent + cloneNode).
  */
  function bindShipPromise() {
    var wrappers = document.querySelectorAll('[data-rossi-ship-promise]');
    if (!wrappers.length) return;

    var modal = null;
    var modalBody = null;
    var lastTrigger = null;

    function svgClose() {
      var ns = 'http://www.w3.org/2000/svg';
      var svg = document.createElementNS(ns, 'svg');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('fill', 'none');
      var p1 = document.createElementNS(ns, 'path');
      p1.setAttribute('d', 'M3.33 3.33L16.67 16.67');
      p1.setAttribute('stroke', 'currentColor');
      p1.setAttribute('stroke-width', '1.5');
      p1.setAttribute('stroke-linecap', 'round');
      p1.setAttribute('stroke-linejoin', 'round');
      var p2 = document.createElementNS(ns, 'path');
      p2.setAttribute('d', 'M3.33 16.67L16.67 3.33');
      p2.setAttribute('stroke', 'currentColor');
      p2.setAttribute('stroke-width', '1.5');
      p2.setAttribute('stroke-linecap', 'round');
      p2.setAttribute('stroke-linejoin', 'round');
      svg.appendChild(p1);
      svg.appendChild(p2);
      return svg;
    }

    function buildModal() {
      if (modal) return modal;
      modal = document.createElement('div');
      modal.className = 'rossi-ship-promise-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-hidden', 'true');

      var overlay = document.createElement('div');
      overlay.className = 'rossi-ship-promise-modal__overlay';
      overlay.setAttribute('data-rossi-ship-overlay', '');

      var card = document.createElement('div');
      card.className = 'rossi-ship-promise-modal__card';
      card.setAttribute('role', 'document');

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'rossi-ship-promise-modal__close';
      closeBtn.setAttribute('data-rossi-ship-close', '');
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.appendChild(svgClose());

      modalBody = document.createElement('div');
      modalBody.className = 'rossi-ship-promise-modal__body';
      modalBody.setAttribute('data-rossi-ship-modal-body', '');

      card.appendChild(closeBtn);
      card.appendChild(modalBody);
      modal.appendChild(overlay);
      modal.appendChild(card);
      document.body.appendChild(modal);

      // Close handlers (delegated on overlay + close button)
      modal.addEventListener('click', function (e) {
        if (e.target.closest('[data-rossi-ship-overlay]') || e.target.closest('[data-rossi-ship-close]')) {
          closeModal();
        }
      });
      return modal;
    }

    function clearChildren(node) {
      if (typeof node.replaceChildren === 'function') {
        node.replaceChildren();
      } else {
        while (node.firstChild) node.removeChild(node.firstChild);
      }
    }

    function openModal(state, wrapper) {
      buildModal();
      var tpl = wrapper.querySelector('template[data-rossi-ship-content="' + state + '"]');
      if (!tpl || !modalBody) return;
      clearChildren(modalBody);
      modalBody.appendChild(tpl.content.cloneNode(true));
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('has-rossi-ship-modal-open');
      var pill = wrapper.querySelector('[data-rossi-ship-pill]');
      if (pill) {
        pill.setAttribute('aria-expanded', 'true');
        lastTrigger = pill;
      }
      var closeBtn = modal.querySelector('[data-rossi-ship-close]');
      if (closeBtn) {
        try { closeBtn.focus({ preventScroll: true }); } catch (e) { closeBtn.focus(); }
      }
    }

    function closeModal() {
      if (!modal || !modal.classList.contains('is-open')) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('has-rossi-ship-modal-open');
      document.querySelectorAll('[data-rossi-ship-pill]').forEach(function (p) {
        p.setAttribute('aria-expanded', 'false');
      });
      if (lastTrigger) {
        try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); }
        lastTrigger = null;
      }
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('is-open')) {
        closeModal();
      }
    });

    wrappers.forEach(function (wrapper) {
      var pill = wrapper.querySelector('[data-rossi-ship-pill]');
      var label = wrapper.querySelector('[data-rossi-ship-label]');
      var variantsScript = wrapper.querySelector('script[data-rossi-ship-variants]');
      var threshold = parseInt(wrapper.getAttribute('data-threshold'), 10) || 200000;

      if (!pill || !label) return;

      var variantPrices = {};
      if (variantsScript) {
        try { variantPrices = JSON.parse(variantsScript.textContent || '{}'); }
        catch (e) { variantPrices = {}; }
      }

      var underTpl = wrapper.querySelector('template[data-rossi-ship-content="under"]');
      var overTpl = wrapper.querySelector('template[data-rossi-ship-content="over"]');
      var underLabel = '';
      var overLabel = '';
      if (underTpl) {
        var u = underTpl.content.querySelector('.rossi-ship-promise__pop-label-under');
        if (u) underLabel = u.textContent.trim();
      }
      if (overTpl) {
        var o = overTpl.content.querySelector('.rossi-ship-promise__pop-label-over');
        if (o) overLabel = o.textContent.trim();
      }

      function applyState(price) {
        var isOver = price >= threshold;
        pill.classList.toggle('is-over', isOver);
        pill.classList.toggle('is-under', !isOver);
        if (isOver && overLabel) label.textContent = overLabel;
        else if (!isOver && underLabel) label.textContent = underLabel;
        pill.setAttribute('data-rossi-state', isOver ? 'over' : 'under');
      }

      var initialState = pill.classList.contains('is-over') ? 'over' : 'under';
      pill.setAttribute('data-rossi-state', initialState);

      pill.addEventListener('click', function () {
        var state = pill.getAttribute('data-rossi-state') || 'under';
        openModal(state, wrapper);
      });

      function onVariantChange(variantId) {
        if (!variantId) return;
        var price = variantPrices[String(variantId)];
        if (typeof price === 'number') applyState(price);
      }

      var variantSelect = document.querySelector('form[action*="/cart/add"] select[name="id"], form[data-product-form] select[name="id"]');
      if (variantSelect) {
        variantSelect.addEventListener('change', function () {
          onVariantChange(variantSelect.value);
        });
      }

      var lastVariantParam = (new URL(window.location.href)).searchParams.get('variant');
      setInterval(function () {
        var current = (new URL(window.location.href)).searchParams.get('variant');
        if (current && current !== lastVariantParam) {
          lastVariantParam = current;
          onVariantChange(current);
        }
      }, 500);

      ['variant:change', 'rio:variant-change', 'product:variant-change'].forEach(function (evt) {
        document.addEventListener(evt, function (e) {
          var v = (e.detail && (e.detail.variant || e.detail)) || null;
          if (v && v.id) onVariantChange(v.id);
        });
      });
    });
  }

  /* ===== STICKY BOTTOM BAR =====
     Shows after primary CTA scrolls past viewport top.
     Bridges sticky qty + ATC back to main product form.
  */
  function bindStickyBar() {
    var bar = document.querySelector('[data-rossi-sticky-bar]');
    if (!bar) return;

    var primaryCta = document.querySelector('.rossi-cta-row') || document.querySelector('.product-form__cart-submit');
    var mainForm = document.querySelector('form[data-product-form]');
    var mainAtc = mainForm ? mainForm.querySelector('[data-add-to-cart], button[name="add"]') : null;
    var mainQty = mainForm ? mainForm.querySelector('select.product-form__input--quantity, input.product-form__input--quantity, [name="quantity"]') : null;
    var stickyQty = bar.querySelector('[data-rossi-sticky-qty]');
    var stickyAtc = bar.querySelector('[data-rossi-sticky-atc]');

    function show() { bar.classList.add('is-visible'); bar.setAttribute('aria-hidden', 'false'); }
    function hide() { bar.classList.remove('is-visible'); bar.setAttribute('aria-hidden', 'true'); }

    function check() {
      if (!primaryCta) { show(); return; }
      var ctaRect = primaryCta.getBoundingClientRect();
      var ctaOut = ctaRect.bottom < 40;
      if (ctaOut) show(); else hide();
    }

    var raf = null;
    function onScroll() {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = null; check(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    check();

    if (stickyQty && mainQty) {
      stickyQty.addEventListener('change', function () {
        mainQty.value = stickyQty.value;
        mainQty.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (mainQty.value) {
        var opt = stickyQty.querySelector('option[value="' + mainQty.value + '"]');
        if (opt) stickyQty.value = mainQty.value;
      }
    }

    if (stickyAtc) {
      stickyAtc.addEventListener('click', function (e) {
        e.preventDefault();
        if (stickyQty && mainQty) {
          mainQty.value = stickyQty.value;
          mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (mainAtc) {
          mainAtc.click();
        } else if (mainForm) {
          mainForm.submit();
        }
      });
    }
  }

  /* ===== QUICK-SHOP GIFT MODAL =====
     Offer Card "Claim Your Gift" button opens a popup quick-shop of the
     gift product. Fetches /products/{handle}.js + /cart.js, renders the
     product via safe DOM construction (no innerHTML of fetched data),
     gates Add-to-Cart on the cart subtotal threshold, and adds via the
     theme's window.blogCartBridge (adds item + refreshes + opens the
     native cart drawer). Falls back to a /cart redirect if the bridge
     is unavailable. */
  var qsModal, qsOverlay, qsBody, qsLastFocused;
  var qsProductCache = {};

  function bindQuickShop() {
    qsModal = document.getElementById('rossi-quick-shop-modal');
    if (!qsModal) return;
    qsOverlay = qsModal.querySelector('.rossi-modal__overlay');
    qsBody = qsModal.querySelector('[data-rossi-quick-shop-body]');

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-rossi-quick-shop]');
      if (trigger) {
        e.preventDefault();
        openQuickShop(trigger);
      }
    });

    qsModal.addEventListener('click', function (e) {
      if (e.target.closest('[data-rossi-modal-close]') || e.target === qsOverlay) {
        closeQuickShop();
        return;
      }
      var atc = e.target.closest('[data-rossi-qs-atc]');
      if (atc) {
        e.preventDefault();
        quickShopAddToCart(atc);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && qsModal.classList.contains('is-open')) closeQuickShop();
    });
  }

  function openQuickShop(trigger) {
    if (!qsModal) return;
    qsLastFocused = document.activeElement;
    var handle = trigger.getAttribute('data-product-handle');
    var threshold = parseFloat(trigger.getAttribute('data-threshold')) || 0;

    qsModal.classList.add('is-open');
    qsModal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    /* Hook for CSS to hide max-z 3rd-party widgets (chat, Swym, sticky bar)
       that would otherwise poke over the modal — they use z-index 2147483647. */
    document.body.classList.add('rossi-quick-shop-open');
    qsPlaceholder('Loading…');
    setTimeout(function () {
      var f = qsModal.querySelector('.rossi-modal__close');
      if (f) f.focus();
    }, 100);

    if (!handle) { qsPlaceholder('Gift unavailable.'); return; }

    var productPromise = qsProductCache[handle]
      ? Promise.resolve(qsProductCache[handle])
      : fetch('/products/' + encodeURIComponent(handle) + '.js').then(function (r) {
          if (!r.ok) throw new Error('product fetch failed');
          return r.json();
        });

    Promise.all([
      productPromise,
      fetch('/cart.js').then(function (r) { return r.json(); })
    ]).then(function (res) {
      qsProductCache[handle] = res[0];
      renderQuickShop(res[0], res[1], threshold);
    }).catch(function () {
      qsPlaceholder('Sorry — this gift is unavailable right now.');
    });
  }

  function closeQuickShop() {
    if (!qsModal) return;
    qsModal.classList.remove('is-open');
    qsModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('rossi-quick-shop-open');
    if (qsLastFocused && qsLastFocused.focus) qsLastFocused.focus();
  }

  /* DOM helper — builds an element with attrs + children. The `text` key
     sets textContent (escaped by the DOM). No innerHTML of fetched data. */
  function qsEl(tag, attrs, kids) {
    var el = document.createElement(tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function (k) {
      if (k === 'text') el.textContent = attrs[k];
      else if (attrs[k] != null) el.setAttribute(k, attrs[k]);
    });
    (kids || []).forEach(function (c) { if (c) el.appendChild(c); });
    return el;
  }

  function qsMoney(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function qsPlaceholder(text) {
    if (!qsBody) return;
    qsBody.replaceChildren(qsEl('p', { 'class': 'rossi-quick-shop__placeholder', text: text }));
  }

  function renderQuickShop(product, cart, threshold) {
    if (!qsBody) return;
    var variants = product.variants || [];
    var hasAvailable = variants.some(function (v) { return v.available; });
    var firstVariant = variants[0] || {};

    /* Eligible subtotal = sum of non-gift cart line finals (gift excluded). */
    var eligible = 0;
    var alreadyInCart = false;
    (cart.items || []).forEach(function (it) {
      if (it.product_id === product.id) { alreadyInCart = true; }
      else { eligible += it.final_line_price; }
    });
    var thresholdCents = Math.round(threshold * 100);
    var unlocked = eligible >= thresholdCents;
    var remaining = Math.max(0, thresholdCents - eligible);

    var img = product.featured_image || (product.images && product.images[0]) || '';
    var desc = (product.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (desc.length > 220) desc = desc.slice(0, 220).replace(/\s+\S*$/, '') + '…';

    var info = [];
    info.push(qsEl('p', { 'class': 'rossi-quick-shop__eyebrow', text: 'Your Gift' }));
    info.push(qsEl('h2', { 'class': 'rossi-quick-shop__title', id: 'rossi-qs-title', text: product.title }));
    info.push(qsEl('p', { 'class': 'rossi-quick-shop__price' }, [
      qsEl('span', { 'class': 'rossi-quick-shop__price-was', text: qsMoney(firstVariant.price != null ? firstVariant.price : product.price) }),
      qsEl('span', { 'class': 'rossi-quick-shop__price-free', text: 'Free' })
    ]));
    if (desc) info.push(qsEl('p', { 'class': 'rossi-quick-shop__desc', text: desc }));

    if (variants.length > 1) {
      var sel = qsEl('select', { 'class': 'rossi-quick-shop__variant', 'data-rossi-qs-variant': '', 'aria-label': 'Choose option' });
      variants.forEach(function (v) {
        var opt = qsEl('option', { value: v.id, text: v.title + (v.available ? '' : ' — Sold out') });
        if (!v.available) opt.disabled = true;
        sel.appendChild(opt);
      });
      info.push(sel);
    }

    if (!alreadyInCart && !unlocked) {
      var pct = thresholdCents > 0 ? Math.min(100, Math.round(eligible / thresholdCents * 100)) : 0;
      var bar = qsEl('span');
      bar.style.width = pct + '%';
      info.push(qsEl('div', { 'class': 'rossi-quick-shop__gate' }, [
        qsEl('div', { 'class': 'rossi-quick-shop__progress' }, [bar]),
        qsEl('p', { 'class': 'rossi-quick-shop__gate-msg' }, [
          document.createTextNode('Spend '),
          qsEl('strong', { text: qsMoney(remaining) }),
          document.createTextNode(' more to unlock your free gift.')
        ])
      ]));
    }

    var atcDisabled, atcLabel;
    if (alreadyInCart) { atcDisabled = true; atcLabel = '✓ Gift already in your cart'; }
    else if (!hasAvailable) { atcDisabled = true; atcLabel = 'Sold Out'; }
    else if (!unlocked) { atcDisabled = true; atcLabel = 'Spend ' + qsMoney(remaining) + ' more to unlock'; }
    else { atcDisabled = false; atcLabel = 'Add Gift to Cart'; }

    var atc = qsEl('button', {
      type: 'button',
      'class': 'rossi-quick-shop__atc',
      'data-rossi-qs-atc': '',
      'data-variant-id': firstVariant.id || '',
      text: atcLabel
    });
    if (atcDisabled) atc.disabled = true;
    info.push(atc);
    info.push(qsEl('a', {
      'class': 'rossi-quick-shop__link',
      href: '/products/' + product.handle,
      text: 'View full product details'
    }));

    var children = [];
    if (img) {
      children.push(qsEl('div', { 'class': 'rossi-quick-shop__media' }, [
        qsEl('img', { src: img, alt: product.title, loading: 'lazy' })
      ]));
    }
    children.push(qsEl('div', { 'class': 'rossi-quick-shop__info' }, info));
    qsBody.replaceChildren(qsEl('div', { 'class': 'rossi-quick-shop__inner' }, children));
  }

  function quickShopAddToCart(btn) {
    if (btn.disabled) return;
    var sel = qsBody && qsBody.querySelector('[data-rossi-qs-variant]');
    var variantId = sel ? sel.value : btn.getAttribute('data-variant-id');
    if (!variantId) return;

    btn.disabled = true;
    var orig = btn.textContent;
    btn.textContent = 'Adding…';

    function fail() {
      btn.disabled = false;
      btn.textContent = orig;
      if (qsBody && !qsBody.querySelector('.rossi-quick-shop__error')) {
        qsBody.appendChild(qsEl('p', {
          'class': 'rossi-quick-shop__error',
          role: 'alert',
          text: 'Could not add the gift. Please try again.'
        }));
      }
    }

    if (typeof window.blogCartBridge === 'function') {
      /* Guard: blogCartBridge is a theme global — if a theme update ever
         makes it return a non-Promise (or throw), don't blow up with an
         uncaught TypeError that leaves the button stuck on "Adding…". */
      var bridgeResult;
      try {
        bridgeResult = window.blogCartBridge({ id: variantId, quantity: 1 });
      } catch (e) {
        bridgeResult = null;
      }
      if (bridgeResult && typeof bridgeResult.then === 'function') {
        bridgeResult.then(function () { closeQuickShop(); }).catch(fail);
      } else {
        fail();
      }
    } else {
      /* Fallback: direct add, then go to the cart page. */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        window.location.href = '/cart';
      }).catch(fail);
    }
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        resolve();
      } catch (e) { reject(e); }
    });
  }

  /* ===== MAKE OFFER MODAL ===== */
  var modal, modalOverlay, lastFocused;

  function bindMakeOfferModal() {
    modal = document.getElementById('rossi-make-offer-modal');
    if (!modal) return;
    modalOverlay = modal.querySelector('.rossi-modal__overlay');

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-rossi-make-offer]');
      if (trigger) {
        e.preventDefault();
        openModal();
      }
    });

    modal.addEventListener('click', function (e) {
      if (e.target.closest('[data-rossi-modal-close]') || e.target === modalOverlay) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    // Legacy fallback: if URL still has contact_posted=true (e.g. user came
    // from non-AJAX submission on old cached theme), clean it. AJAX path no
    // longer adds these params.
    if (history.replaceState && location.search.indexOf('contact_posted=true') !== -1) {
      var cleanSearch = location.search
        .replace(/[?&]contact_posted=true/, '')
        .replace(/^&/, '?');
      if (cleanSearch === '?') cleanSearch = '';
      history.replaceState(null, '', location.pathname + cleanSearch);
    }
  }

  function openModal() {
    if (!modal) return;
    lastFocused = document.activeElement;
    modal.classList.add('is-open');
    modal.removeAttribute('aria-hidden');
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      var firstInput = modal.querySelector('input, textarea, button');
      if (firstInput) firstInput.focus();
    }, 100);
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  /* ===== SCROLL REVEAL ===== */
  function bindScrollReveals() {
    if (!('IntersectionObserver' in window)) return;
    var targets = document.querySelectorAll('.rossi-reveal');
    if (!targets.length) return;
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0.1 });
    targets.forEach(function (el) { obs.observe(el); });
  }

  /* ===== STOCK CARD BAR ANIMATION ON REVEAL ===== */
  function bindStockCardAnimation() {
    var cards = document.querySelectorAll('.rossi-stock-card');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach(activateStockCard);
      return;
    }
    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          activateStockCard(entry.target);
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -60px 0px', threshold: 0.3 });
    cards.forEach(function (el) { obs.observe(el); });
  }

  function activateStockCard(card) {
    var fill = card.querySelector('.rossi-stock-card__bar-fill');
    if (!fill) return;
    var target = fill.dataset.percent || '100';
    fill.style.width = '0%';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        fill.style.width = target + '%';
      });
    });
  }
})();

/* =========================================================================
   ROSSI v2 — Adaptive Gallery Aspect (re-introduced 2026-05-08)
   Sets --rossi-slide-aspect on .pwzrswiper-container based on the active
   slide's natural image dimensions. CSS reads var() with 4/5 fallback, so
   if this script never runs, gallery still looks correct (portrait frame).

   Avoids prior failure modes:
     - NO health-check class (prior version's thumbStripHorizontal test
       always failed → fallback class permanently locked → CSS dead).
       This script never adds a class to body — pure inline custom property.
     - Race-safe: MutationObserver scoped to .product__media-container
       catches Rio late mount + slide rebuilds. Idempotent via data-attr
       guard. If Rio replaces the container element, retry interval rebinds
       within 200ms during first 6s.
     - If .naturalWidth = 0 (image not yet loaded), one-shot 'load'
       listener applies aspect when ready.
   ========================================================================= */
(function rossiGalleryAspect() {
  if (!document.body || !document.body.classList.contains('template-v2')) return;

  function readImg(img) {
    var w = img && img.naturalWidth;
    var h = img && img.naturalHeight;
    if (!w || !h) return 0;
    return w / h;
  }

  function applyAspect(container, aspect) {
    if (!container || !aspect) return;
    var rounded = Math.round(aspect * 1000) / 1000;
    if (container.dataset.rossiAspectVal === String(rounded)) return;
    container.dataset.rossiAspectVal = String(rounded);
    container.style.setProperty('--rossi-slide-aspect', rounded);
  }

  function syncContainer(container) {
    var slide = container.querySelector('.pwzrswiper-slide-active')
              || container.querySelector('.pwzrswiper-slide');
    if (!slide) return;
    var img = slide.querySelector('img');
    if (!img) return;
    var aspect = readImg(img);
    if (aspect) {
      applyAspect(container, aspect);
    } else {
      // Image not loaded yet — bind one-shot listener
      img.addEventListener('load', function () {
        var a = readImg(img);
        if (a) applyAspect(container, a);
      }, { once: true });
    }
  }

  function bindContainer(container) {
    if (container.dataset.rossiAspectBound) return;
    container.dataset.rossiAspectBound = '1';
    syncContainer(container);
    var obs = new MutationObserver(function () { syncContainer(container); });
    obs.observe(container, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true
    });
  }

  function tryBind() {
    var c = document.querySelector('.product__media-container .pwzrswiper-container');
    if (c) bindContainer(c);
  }

  function start() {
    tryBind();
    // Watch gallery wrap for Rio mount + rebuilds
    var wrap = document.querySelector('.product__media-container');
    if (wrap) {
      var wrapObs = new MutationObserver(tryBind);
      wrapObs.observe(wrap, { childList: true, subtree: true });
    }
    // Retry briefly while Rio mounts (typical 1–3s)
    var attempts = 0;
    var retry = setInterval(function () {
      tryBind();
      if (++attempts >= 30) clearInterval(retry);
    }, 200);
    // Re-sync on viewport resize (guards any rounding drift)
    window.addEventListener('resize', function () {
      var c = document.querySelector('.product__media-container .pwzrswiper-container');
      if (c) syncContainer(c);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* =========================================================================
   ROSSI v2 — Mulberry Protection compact trigger
   Industry pattern (Affirm-style): single inline row with dynamic plan price.
   Click expands plans inline. Replaces full warranty card on PDP.
   - Reads $price from Mulberry's [data-price-slot="2yr"] populated by their JS
   - Updates trigger label "Add Protection Plan from $X.XX"
   - Toggles .is-expanded on .mulberry-pdp on header click
   - Re-runs on variant change (Mulberry re-populates price slot per variant)
   ========================================================================= */
(function rossiMulberryCompact() {
  if (!document.body.classList.contains('template-v2')) return;

  function updateTriggers() {
    document.querySelectorAll('.mulberry-pdp').forEach(function (card) {
      var heading = card.querySelector('.mulberry-pdp__heading');
      var price2yr = card.querySelector('[data-price-slot="2yr"]');
      if (!heading || !price2yr) return;
      var t = price2yr.textContent.trim();
      if (t && t !== '—' && t !== '') {
        heading.textContent = 'Add Protection Plan from ' + t;
      }
    });
  }

  // Click toggle on the compact header (don't trigger on plan radios)
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('.mulberry-pdp__header');
    if (!trigger) return;
    if (e.target.closest('.mulberry-pdp__plans')) return;
    e.preventDefault();
    var card = trigger.closest('.mulberry-pdp');
    if (card) card.classList.toggle('is-expanded');
  });

  // Initial label populate (Mulberry JS may need a moment)
  setTimeout(updateTriggers, 1500);
  setTimeout(updateTriggers, 3500);

  // Re-run on variant change (price slot may update)
  document.addEventListener('variant:change', function () { setTimeout(updateTriggers, 600); });
  document.addEventListener('product:variant:change', function () { setTimeout(updateTriggers, 600); });

  // Watch for late-loaded price slot text via MutationObserver
  var priceSlot = document.querySelector('.mulberry-pdp [data-price-slot="2yr"]');
  if (priceSlot) {
    var obs = new MutationObserver(function () { updateTriggers(); });
    obs.observe(priceSlot, { childList: true, characterData: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 8000);
  }

  window.__rossiMulberryCompactUpdate = updateTriggers;
})();

/* =========================================================================
   ROSSI v2 — Move compact stepper INTO .rossi-cta-row
   Industry pattern (Wayfair, Article, RH): qty stepper inline with ATC.
   The legacy .product-form__quantity block contains:
     - .product-form__quantity-button (− subtract)
     - input.product-form__input--quantity (number)
     - .product-form__quantity-button (+ add)
     - select.product-form__input--quantity (dropdown — hidden via CSS)
   This IIFE relocates the entire block as first child of .rossi-cta-row,
   wrapped in .rossi-cta-row__stepper-box. CSS unhides the +/-/input,
   hides the select. Click handler increments/decrements input value.
   The form's name="quantity" input is the same — form submission works.
   ========================================================================= */
(function rossiCtaStepperMove() {
  if (!document.body.classList.contains('template-v2')) return;

  function moveStepper() {
    if (document.querySelector('.rossi-cta-row__stepper-box')) return true;
    var qtyBlock = document.querySelector('.product-form__quantity');
    var ctaRow = document.querySelector('.rossi-cta-row');
    if (!qtyBlock || !ctaRow) return false;
    var box = document.createElement('div');
    box.className = 'rossi-cta-row__stepper-box';
    box.appendChild(qtyBlock);
    ctaRow.insertBefore(box, ctaRow.firstChild);
    return true;
  }

  if (moveStepper()) return;

  var obs = new MutationObserver(function () {
    if (moveStepper()) obs.disconnect();
  });
  obs.observe(document.body, { childList: true, subtree: true });
  setTimeout(function () { obs.disconnect(); }, 6000);

  // Wire +/- click handlers (in case theme JS doesn't fire outside legacy wrapper)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.rossi-cta-row__stepper-box .product-form__quantity-button');
    if (!btn) return;
    e.preventDefault();
    var inp = btn.closest('.product-form__quantity').querySelector('input.product-form__input--quantity');
    if (!inp) return;
    var cur = parseInt(inp.value, 10) || 1;
    var isAdd = btn.classList.contains('product-form__quantity-add-item') || /add/i.test(btn.className);
    inp.value = isAdd ? Math.min(cur + 1, 99) : Math.max(cur - 1, 1);
    inp.dispatchEvent(new Event('change', { bubbles: true }));
  });
})();

/* =========================================================================
   ROSSI v2 — Bridge v2 ATC to Theme's Existing AJAX Cart Handler
   Theme (theme.js ~24192) has a jQuery click handler bound to:
     .product-form__inner .product-form__controls-group--submit
       .product-form__item--submit button.product-form__cart-submit
   This handler does AJAX /cart/add.js, calls fetchCart(), fires r$1('cart:updated')
   which auto-opens drawer with rendered items via cartDrawer module.

   v2 ATC (.rossi-btn--atc in .rossi-cta-row) does NOT match that selector,
   so theme handler never fires → form submits natively → /cart page redirect.

   Fix: on v2 form submit, programmatically click the LEGACY hidden ATC button.
   Legacy handler reads qty from .product-form__quantity input (same input
   our stepper uses), runs full theme cart pipeline (AJAX + drawer + Mulberry).
   SCOPE: body.template-v2 PDPs only.

   ⚠ INFINITE-LOOP GUARD (bridgeBusy):
   theme.js:24193 only calls preventDefault when `.product-form__inner
   .set-included.hide` is absent. On bundle/sectional products with the
   "Set Includes" block where items haven't been selected, that element
   stays present → handler is a no-op → click default re-submits the form
   → bridge captures again → infinite recursion → tab freezes, cart never
   adds. The reentry guard short-circuits the nested submit so the second
   submission proceeds natively (page redirect to /cart, same as theme's
   pre-bridge fallback).
   ========================================================================= */
(function rossiAjaxAddToCart() {
  if (!document.body.classList.contains('template-v2')) return;

  var bridgeBusy = false;
  /* Idempotency debounce: timestamp of the last ACCEPTED ATC trigger.
     Rapid repeat clicks / double-taps within the in-flight window would
     otherwise each fire a /cart/add.js POST → nondeterministic cart qty
     (verified: 3 fast clicks → 3 POSTs → cart shows 2). Time-based so it
     self-clears and can never leave the button permanently stuck. */
  var rossiLastAtcAt = 0;
  var ROSSI_ATC_DEBOUNCE_MS = 1500;

  /* Mulberry warranty adapter (ottawa.getmulberry.com/app/adapter.js) binds a
     click listener on .rossi-btn--atc that calls e.preventDefault() then errors
     out ("a?.updateOffer is not a function"), killing the submit chain so the
     form's submit event never fires. Capture-phase click handler below runs
     BEFORE Mulberry, stops propagation, and forwards to the legacy hidden ATC
     button which is not intercepted. Theme's jQuery handler on legacy ATC then
     runs AJAX /cart/add.js + drawer open as normal. */
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.rossi-btn--atc');
    if (!btn) return;
    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    var formId = btn.getAttribute('form');
    var form = formId ? document.getElementById(formId) : btn.closest('form[action*="/cart/add"]');
    if (!form) return;
    var legacyAtc = form.querySelector(
      '.product-form__controls-group--submit .product-form__item--submit button.product-form__cart-submit'
    );
    if (!legacyAtc || legacyAtc === btn) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    /* Idempotency: swallow rapid repeat clicks within the in-flight window. */
    if (Date.now() - rossiLastAtcAt < ROSSI_ATC_DEBOUNCE_MS) return;
    rossiLastAtcAt = Date.now();

    var qtyInput = form.querySelector(
      '.product-form__quantity input.product-form__input--quantity'
    );
    if (qtyInput && qtyInput.value) {
      qtyInput.value = String(parseInt(qtyInput.value, 10) || 1);
    }

    var wasDisabled = legacyAtc.hasAttribute('disabled');
    var wasAriaDisabled = legacyAtc.getAttribute('aria-disabled');
    if (wasDisabled) legacyAtc.removeAttribute('disabled');
    if (wasAriaDisabled) legacyAtc.removeAttribute('aria-disabled');

    bridgeBusy = true;
    try {
      legacyAtc.click();
    } finally {
      bridgeBusy = false;
      if (wasDisabled) legacyAtc.setAttribute('disabled', 'disabled');
      if (wasAriaDisabled) legacyAtc.setAttribute('aria-disabled', wasAriaDisabled);
    }
  }, true); // capture phase — must run before Mulberry's bubble listener

  document.addEventListener('submit', function (e) {
    if (bridgeBusy) return; // nested submit from legacy click default — let it pass through
    var form = e.target.closest && e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    if (!form.classList.contains('product-form')) return;

    // Find the legacy hidden ATC that theme's jQuery handler is bound to
    var legacyAtc = form.querySelector(
      '.product-form__controls-group--submit .product-form__item--submit button.product-form__cart-submit'
    );

    if (!legacyAtc) {
      // No legacy button found — let native submit proceed (page redirect)
      return;
    }

    // Stop our v2 button's native form submit; theme's click handler on the
    // legacy ATC will fire AJAX add + drawer open. Quantity is read from the
    // shared .product-form__quantity input which our stepper already updates.
    e.preventDefault();
    e.stopPropagation();

    /* Idempotency: swallow rapid repeat submits within the in-flight window. */
    if (Date.now() - rossiLastAtcAt < ROSSI_ATC_DEBOUNCE_MS) return;
    rossiLastAtcAt = Date.now();

    // Sync v2 qty input value into the form before legacy handler reads it
    var qtyInput = form.querySelector(
      '.product-form__quantity input.product-form__input--quantity'
    );
    if (qtyInput && qtyInput.value) {
      // Normalize "01" → "1" so theme's parseFloat doesn't choke
      qtyInput.value = String(parseInt(qtyInput.value, 10) || 1);
    }

    // Trigger the legacy click — theme handler does the rest.
    // Force-strip disabled state: theme.js updateBuyButton may have
    // transiently disabled the legacy ATC when chip click fired with
    // missing variant payload. Browser blocks .click() on disabled
    // buttons, so without this guard cart-add silently no-ops.
    var wasDisabled = legacyAtc.hasAttribute('disabled');
    var wasAriaDisabled = legacyAtc.getAttribute('aria-disabled');
    if (wasDisabled) legacyAtc.removeAttribute('disabled');
    if (wasAriaDisabled) legacyAtc.removeAttribute('aria-disabled');

    bridgeBusy = true;
    try {
      legacyAtc.click();
    } finally {
      bridgeBusy = false;
      // Restore prior disabled state (theme.js will overwrite shortly)
      if (wasDisabled) legacyAtc.setAttribute('disabled', 'disabled');
      if (wasAriaDisabled) legacyAtc.setAttribute('aria-disabled', wasAriaDisabled);
    }
  }, true); // capture phase, runs before form's default submit

  // Defensive helpers for debugging
  window.__rossiCart = {
    triggerLegacy: function () {
      var btn = document.querySelector(
        '.product-form__controls-group--submit .product-form__item--submit button.product-form__cart-submit'
      );
      if (btn) btn.click();
    }
  };
})();

/* =========================================================================
   ROSSI — Cart drawer image fallback via altText match
   Cart line item image = variant.featured_image when set, else falls back to
   product.featured_image (first product image). For products where variants
   are not linked to images in Shopify admin, this shows wrong image (always
   the first variant's image, regardless of which variant was added).

   Fix: after cart drawer items render, for each cart line, derive product
   handle from line URL, fetch /products/{handle}.js (cached in-memory), find
   the image whose alt contains variant title, replace the rendered <img> src.

   Site-wide (any template). Idempotent via data attr. Skip lines already
   processed.
   ========================================================================= */
(function rossiCartImageSwap() {
  if (!document) return;

  var productCache = {};

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + handle + '.js', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
    return productCache[handle];
  }

  function matchImage(product, variantId) {
    if (!product || !product.variants || !product.images) return null;
    var variant = product.variants.find(function (v) { return String(v.id) === String(variantId); });
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    var title = variant.title || '';
    if (!title) return null;
    var media = product.media || [];
    for (var i = 0; i < media.length; i++) {
      var m = media[i];
      if (m && m.media_type === 'image' && m.alt && m.alt.indexOf(title) !== -1) {
        return (m.preview_image && m.preview_image.src) || m.src;
      }
    }
    // Fallback: images array
    for (var j = 0; j < product.images.length; j++) {
      var im = product.images[j];
      if (im && typeof im === 'string' && im.indexOf(title) !== -1) return im;
    }
    return null;
  }

  function sizedUrl(src, width) {
    if (!src) return src;
    // Add Shopify ?width= param if not present
    if (src.indexOf('width=') !== -1) return src;
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + (width || 400);
  }

  function processItem(itemEl) {
    if (!itemEl || itemEl.hasAttribute('data-rossi-img-fixed')) return;
    var variantId = itemEl.getAttribute('data-variant-id') || itemEl.getAttribute('data-id');
    var link = itemEl.querySelector('a[href*="/products/"]');
    if (!variantId || !link) return;
    var match = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    if (!match) return;
    var handle = match[1];
    itemEl.setAttribute('data-rossi-img-fixed', '1');
    fetchProduct(handle).then(function (product) {
      var newSrc = matchImage(product, variantId);
      if (!newSrc) return;
      var img = itemEl.querySelector('img');
      if (!img) return;
      var finalSrc = sizedUrl(newSrc, 400);
      if (finalSrc.indexOf('//') === 0) finalSrc = 'https:' + finalSrc;
      if (img.src !== finalSrc) {
        img.removeAttribute('srcset');
        img.src = finalSrc;
      }
    });
  }

  function scan() {
    var items = document.querySelectorAll('.cart__item[data-variant-id], .cart__item[data-id]');
    items.forEach(processItem);
  }

  // Observe cart drawer for renders
  function init() {
    scan();
    var drawer = document.querySelector('[data-cart-drawer], .cart-drawer, #cart-drawer, .quick-cart, .js-items');
    var target = drawer || document.body;
    var mo = new MutationObserver(function () {
      // Reset processed flag on items that may have been re-rendered with new variant
      // (Shopify re-renders entire list, so flags get cleared automatically with new DOM)
      scan();
    });
    try {
      mo.observe(target, { childList: true, subtree: true });
    } catch (e) {}

    // Also re-scan on common cart events
    ['cart:updated', 'cart:build', 'cart:open', 'flu:cart:updated', 'product:added', 'apps:product-added-to-cart'].forEach(function (evt) {
      document.addEventListener(evt, function () { setTimeout(scan, 200); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* =========================================================================
   ROSSI v2 — Stock card: click-to-copy SKU + notify-me email submit
   ========================================================================= */
(function () {
  if (!document.body.classList.contains('template-v2')) return;

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-sku-copy]');
    if (!btn) return;
    e.preventDefault();
    var sku = btn.getAttribute('data-sku') || '';
    if (!sku) return;

    var done = function () {
      btn.classList.add('is-copied');
      clearTimeout(btn._copyTimer);
      btn._copyTimer = setTimeout(function () {
        btn.classList.remove('is-copied');
      }, 1500);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(sku).then(done).catch(function () {
        fallbackCopy(sku);
        done();
      });
    } else {
      fallbackCopy(sku);
      done();
    }
  });

  function fallbackCopy(text) {
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    } catch (err) {
      console.warn('[rossi-sku] copy fallback failed', err);
    }
  }

  // Notify-me submit: tries Klaviyo → Swym → Shopify /contact fallback
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-notify-form]');
    if (!form) return;
    e.preventDefault();

    var email = form.querySelector('input[name="email"]');
    var submit = form.querySelector('.rossi-stock-card__notify-submit');
    if (!email || !email.value) return;
    var variantId = form.getAttribute('data-variant-id') || '';
    var productId = form.getAttribute('data-product-id') || '';

    if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['identify', { '$email': email.value }]);
      window._learnq.push(['track', 'Requested Restock Notification', {
        VariantID: variantId,
        ProductID: productId
      }]);
      markSubmitted();
      return;
    }

    if (window.SwymCallbacks && Array.isArray(window.SwymCallbacks)) {
      window.SwymCallbacks.push(function (swat) {
        if (typeof swat.subscribeProductInstockAlert === 'function') {
          swat.subscribeProductInstockAlert({
            empi: parseInt(productId, 10),
            epi: parseInt(variantId, 10),
            email: email.value
          }, markSubmitted, markSubmitted);
        } else {
          fallbackContact();
        }
      });
      return;
    }

    fallbackContact();

    function fallbackContact() {
      var fd = new FormData();
      fd.append('form_type', 'contact');
      fd.append('utf8', '✓');
      fd.append('contact[email]', email.value);
      fd.append('contact[subject]', 'Restock notification request');
      fd.append('contact[body]', 'Notify me when variant ' + variantId + ' (product ' + productId + ') is back in stock.');

      fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin' })
        .then(markSubmitted)
        .catch(markSubmitted);
    }

    function markSubmitted() {
      form.classList.add('is-submitted');
      if (submit) submit.textContent = 'Subscribed';
      email.setAttribute('readonly', 'true');
    }
  });
})();

/* =========================================================================
   ROSSI v2 — Variant-Aware Ship-Days (added 2026-05-08)
   Updates the .rossi-status-line__meta text based on currently selected
   variant's price. Threshold + labels read from data-* attrs on the meta
   span (set by Liquid). Variant→price map embedded as adjacent JSON script.

   Threshold default: 200000 cents = $2000 USD
     - price < $2000  → "Ships in 3-7 Days"
     - price >= $2000 → "Ships in 7-12 Days"

   Listens to: select[name="id"] change, URL ?variant= polling, and custom
   variant:change / rio:variant-change / product:variant-change events.
   No-op when In Stock branch is not active (backorder/OOS show their own
   text; the [data-rossi-ship-days-text] hook is absent there).
   ========================================================================= */
(function rossiShipDays() {
  if (!document.body || !document.body.classList.contains('template-v2')) return;

  function parseVariants(scriptEl) {
    if (!scriptEl) return {};
    try { return JSON.parse(scriptEl.textContent || '{}'); }
    catch (e) { return {}; }
  }

  function bind(meta) {
    if (meta.dataset.rossiShipDaysBound) return;
    meta.dataset.rossiShipDaysBound = '1';

    var threshold = parseInt(meta.getAttribute('data-ship-threshold'), 10) || 200000;
    var labelUnder = meta.getAttribute('data-ship-label-under') || 'Ships in 3-7 Days';
    var labelOver = meta.getAttribute('data-ship-label-over') || 'Ships in 7-12 Days';

    var statusLine = meta.closest('.rossi-status-line');
    var scope = statusLine || document;
    var scriptEl = scope.querySelector('script[data-rossi-ship-days-variants]');
    var variantPrices = parseVariants(scriptEl);

    var textEl = meta.querySelector('[data-rossi-ship-days-text]');
    if (!textEl) return; // Backorder / OOS state — do not update

    function applyPrice(price) {
      if (typeof price !== 'number') return;
      var isOver = price >= threshold;
      var next = isOver ? labelOver : labelUnder;
      if (textEl.textContent.trim() !== next) textEl.textContent = next;
    }

    function onVariantChange(variantId) {
      if (!variantId) return;
      var price = variantPrices[String(variantId)];
      applyPrice(price);
    }

    var variantSelect = document.querySelector(
      'form[action*="/cart/add"] select[name="id"], form[data-product-form] select[name="id"]'
    );
    if (variantSelect) {
      variantSelect.addEventListener('change', function () {
        onVariantChange(variantSelect.value);
      });
    }

    var lastVariantParam = (new URL(window.location.href)).searchParams.get('variant');
    setInterval(function () {
      var current = (new URL(window.location.href)).searchParams.get('variant');
      if (current && current !== lastVariantParam) {
        lastVariantParam = current;
        onVariantChange(current);
      }
    }, 500);

    ['variant:change', 'rio:variant-change', 'product:variant-change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var v = (e.detail && (e.detail.variant || e.detail)) || null;
        if (v && v.id) onVariantChange(v.id);
      });
    });
  }

  function start() {
    document.querySelectorAll('[data-rossi-ship-days]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* =========================================================================
   ROSSI v2 — Swatch Tooltip Tap-Toggle (added 2026-05-08)
   Desktop tooltip works via CSS :hover / :focus-visible. Touch devices
   have no hover state, so tap-to-reveal needs explicit class toggle.

   On tap of a swatch with data-rossi-swatch-tooltip, add .is-tooltip-open
   for 2.5s (auto-hide). Other open tooltips close. Variant selection click
   handler unaffected (no preventDefault).
   ========================================================================= */
(function rossiSwatchTooltip() {
  if (!document.body || !document.body.classList.contains('template-v2')) return;

  var TOOLTIP_TTL_MS = 2500;
  var openTimer = null;

  function closeAll() {
    document.querySelectorAll('.product-form__color-swatch.is-tooltip-open').forEach(function (sw) {
      sw.classList.remove('is-tooltip-open');
    });
    if (openTimer) {
      clearTimeout(openTimer);
      openTimer = null;
    }
  }

  document.addEventListener('click', function (e) {
    var sw = e.target.closest && e.target.closest('.product-form__color-swatch[data-rossi-swatch-tooltip]');
    if (!sw) {
      // Tapped elsewhere — close any open tooltip
      closeAll();
      return;
    }
    // Open this swatch tooltip; auto-close after TTL
    closeAll();
    sw.classList.add('is-tooltip-open');
    openTimer = setTimeout(function () {
      sw.classList.remove('is-tooltip-open');
      openTimer = null;
    }, TOOLTIP_TTL_MS);
  });

  // Close tooltip on Escape
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
})();

/* =========================================================================
   ROSSI v2 — Variant Sync (added 2026-05-11)
   Theme.js native variant-change handler doesn't fully cover v2's restructured
   DOM (price span scope mismatch) and Rio gallery (no [data-media-id] hooks).
   This IIFE syncs everything we care about on variant change:
     - [data-product-price] innerHTML (top price)
     - [data-add-to-cart-price] innerHTML (ATC button price appendix)
     - .rossi-btn--atc [data-add-to-cart-text] (handle preorder, sold out)
     - .rossi-btn--atc disabled attr
     - Rio gallery slide swap (via swiper API or thumbnail click fallback)

   Listens to select[name="id"] change, URL ?variant= polling, and custom
   variant:change / rio:variant-change / product:variant-change events.
   Reads variants JSON from script[data-rossi-variants] (emitted by Liquid).
   ========================================================================= */
(function rossiVariantSync() {
  if (!document.body || !document.body.classList.contains('template-v2')) return;

  function parseVariants() {
    var s = document.querySelector('script[data-rossi-variants]');
    if (!s) return [];
    try { return JSON.parse(s.textContent || '[]'); } catch (e) { return []; }
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      try { return window.Shopify.formatMoney(cents, fmt); } catch (e) {}
    }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  function setHtml(el, html) {
    if (!el) return;
    if (el.innerHTML !== html) el.innerHTML = html;
  }

  function setText(el, txt) {
    if (!el) return;
    if (el.textContent.trim() !== txt) el.textContent = txt;
  }

  function updatePrices(v) {
    var money = formatMoney(v.price);
    document.querySelectorAll('[data-product-price]').forEach(function (el) {
      setHtml(el, money);
    });
    document.querySelectorAll('[data-add-to-cart-price]').forEach(function (el) {
      setHtml(el, ' - ' + money);
    });
  }

  function updateAtcText(v) {
    var btn = document.querySelector('.rossi-btn--atc[data-add-to-cart]');
    if (!btn) return;
    var textEl = btn.querySelector('[data-add-to-cart-text]');
    if (!textEl) return;

    // Label only — theme.js owns disabled state via updateBuyButton.
    // We just override preorder label when applicable, since theme.js
    // doesn't know about preorder semantics (writes "Add to Cart" instead).
    var ds = btn.dataset;
    var label;
    if (!v.available) {
      label = ds.langSoldOut || 'Sold out';
    } else if (v.inventory_quantity <= 0 && v.inventory_policy === 'continue') {
      label = 'Pre-Order Now';
    } else {
      label = ds.langAvailable || 'Add to Cart';
    }
    setText(textEl, label);
  }

  function updateMedia(v) {
    if (!v.media_position && !v.media_id && !v.media_image_url) return;
    var idx = v.media_position ? (v.media_position - 1) : 0;
    var success = false;

    // Swiper navigation helper: slideToLoop when loop mode active, else slideTo.
    // Loop mode adds duplicate slides, so slideTo(idx) misses by loopedSlides count.
    function navSwiper(sw, i) {
      if (sw && sw.params && sw.params.loop && typeof sw.slideToLoop === 'function') {
        sw.slideToLoop(i);
      } else if (sw && typeof sw.slideTo === 'function') {
        sw.slideTo(i);
      }
    }

    // Strategy 1: Rio swiper instance direct slide nav (if exposed on container)
    var container = document.querySelector('.product__media-container .pwzrswiper-container');
    if (container && container.swiper && typeof container.swiper.slideTo === 'function') {
      try { navSwiper(container.swiper, idx); success = true; } catch (e) {}
    }

    // Strategy 2: Find inner swiper element and call slide nav on its instance
    if (!success && container) {
      var innerSwiper = container.querySelector('.swiper, .pwzrswiper');
      if (innerSwiper && innerSwiper.swiper && typeof innerSwiper.swiper.slideTo === 'function') {
        try { navSwiper(innerSwiper.swiper, idx); success = true; } catch (e) {}
      }
    }

    // Strategy 3: Click matching Rio thumbnail by index (common selectors)
    if (!success) {
      var thumbSelectors = [
        '.product__media-container .pwzrjss2',
        '.product__media-container [class*="thumb"] img',
        '.product__media-container .product-thumbnails__item-link',
        '.product__media-container .swiper-slide-thumb',
        '.product__media-container [data-pwzrslideindex]'
      ];
      for (var s = 0; s < thumbSelectors.length && !success; s++) {
        var thumbs = document.querySelectorAll(thumbSelectors[s]);
        if (thumbs[idx]) {
          try { thumbs[idx].click(); success = true; } catch (e) {}
        }
      }
    }

    // Strategy 4: Match by data-media-id on Rio slide
    if (!success && v.media_id) {
      var slide = document.querySelector('.product__media-container .pwzrswiper-slide [data-media-id="' + v.media_id + '"], .product__media-container [data-media-id="' + v.media_id + '"]');
      if (slide) {
        try { slide.click(); success = true; } catch (e) {}
      }
    }

    // Strategy 5: Native theme media-wrapper hide/show (fallback for non-Rio layouts)
    if (!success && v.media_id) {
      var allWrappers = document.querySelectorAll('.product__media-container .media-wrapper');
      var matched = document.querySelector('.product__media-container .media-wrapper[data-media-id="' + v.media_id + '"]');
      if (matched && allWrappers.length) {
        allWrappers.forEach(function (m) { m.classList.add('hidden'); });
        matched.classList.remove('hidden');
        success = true;
      }
    }

    // Strategy 6: Last resort — force-overwrite the active Rio slide's <img>
    // src directly using variant.featured_media URL (emitted by Liquid into
    // variants JSON). Works regardless of Rio's internal API. User can still
    // swipe Rio gallery to see other slides; we only swap the visible one.
    if (!success && v.media_image_url) {
      var rioImg = document.querySelector(
        '.product__media-container .pwzrswiper-slide-active img, ' +
        '.product__media-container .pwzrswiper-slide img, ' +
        '.product__media-container .swiper-slide-active img, ' +
        '.product__media-container img'
      );
      if (rioImg) {
        try {
          rioImg.removeAttribute('srcset');
          rioImg.removeAttribute('data-srcset');
          rioImg.src = v.media_image_url;
          if (rioImg.dataset) rioImg.dataset.src = v.media_image_url;
          success = true;
        } catch (e) {}
      }
    }

    if (!success && window.__rossiDebug) {
      console.warn('[rossiVariantSync] media swap failed for variant', v);
    }
  }

  /* Show the stock card matching the selected variant, hide the rest.
     main-product.liquid server-renders one card per variant inside
     [data-rossi-stock-variant] wrappers. Also set the revealed card's bar
     fill — bindStockCardAnimation only animates cards that were visible at
     reveal time, so a card un-hidden here would otherwise show a 0% bar. */
  function updateStockCard(v) {
    var slots = document.querySelectorAll('[data-rossi-stock-variant]');
    if (!slots.length) return;
    slots.forEach(function (slot) {
      var match = slot.getAttribute('data-rossi-stock-variant') === String(v.id);
      slot.hidden = !match;
      if (match) {
        var fill = slot.querySelector('.rossi-stock-card__bar-fill');
        if (fill) fill.style.width = (fill.dataset.percent || '100') + '%';
      }
    });
  }

  function applyVariant(v) {
    if (!v) return;
    updatePrices(v);
    updateAtcText(v);
    updateMedia(v);
    updateStockCard(v);

    // Notify any 3rd-party apps (Rio gallery, review widgets, upsell, etc)
    // that may be listening for variant-change events. Dispatch multiple
    // common event names — apps differ on which they subscribe to.
    // __rossiDispatched flag prevents our own subscriber from looping back.
    var detail = { variant: v, __rossiDispatched: true };
    [
      'shopify:variantChanged',
      'variantChanged',
      'flu:product:variant-change',
      'productVariantChange'
    ].forEach(function (name) {
      try {
        document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
      } catch (e) {}
    });
  }

  function start() {
    var list = parseVariants();
    if (!list.length) return;
    var byId = {};
    list.forEach(function (v) { byId[String(v.id)] = v; });

    function onVariantId(id) {
      if (!id) return;
      var v = byId[String(id)];
      if (v) applyVariant(v);
    }

    var sel = document.querySelector('form[action*="/cart/add"] select[name="id"], form[data-product-form] select[name="id"]');
    if (sel) {
      sel.addEventListener('change', function () {
        setTimeout(function () { onVariantId(sel.value); }, 60);
      });
    }

    var lastVariantParam = (new URL(window.location.href)).searchParams.get('variant');
    setInterval(function () {
      var cur = (new URL(window.location.href)).searchParams.get('variant');
      if (cur && cur !== lastVariantParam) {
        lastVariantParam = cur;
        onVariantId(cur);
      }
    }, 500);

    ['variant:change', 'rio:variant-change', 'product:variant-change', 'flu:product:variant-change'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        // Skip events we ourselves dispatched (avoid loop)
        if (e.detail && e.detail.__rossiDispatched) return;
        var v = (e.detail && (e.detail.variant || e.detail)) || null;
        if (v && v.id) onVariantId(v.id);
      });
    });

    var initId = (new URL(window.location.href)).searchParams.get('variant') || (sel && sel.value);
    if (initId) {
      setTimeout(function () { onVariantId(initId); }, 100);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();